import { z } from "zod";

// TES-1185 (TES-1180 point 1; A-02 2026-10-08) — the planned medication, one
// structured record per patient with its history:
//   "Läkemedelsuppgifterna anges strukturerat en gång i Ella och återanvänds i resten av arbetsflödet."
//   "Den gemensamma strukturerade läkemedelsposten ska visa aktuell version och ändringshistorik."
// Entered at the care meeting (its Recept fields), kept or changed in the
// treatment decision. Stored as append-only versions (PlannedMedicationVersion).

// TES-1241 — and verified in the transfer review's Recept section.
export const PLANNED_MEDICATION_SOURCES = [
  "meeting",
  "treatment_decision",
  "transfer_review",
] as const;
export type PlannedMedicationSource =
  (typeof PLANNED_MEDICATION_SOURCES)[number];

// Where a version came from, as the doctor reads it. Proposed wording.
export const PLANNED_MEDICATION_SOURCE_LABEL: Record<
  PlannedMedicationSource,
  string
> = {
  meeting: "Vårdmötet",
  treatment_decision: "Behandlingsbeslutet",
  transfer_review: "Receptövertaget",
};

// Emelie's six parts, in her order (TES-1180 point 1). "Doseringsintervall"
// is the order's frequency.
export const PLANNED_MEDICATION_FIELD_LABEL = {
  medication: "Läkemedel",
  strength: "Styrka",
  dose: "Dos",
  frequency: "Doseringsintervall",
  duration: "Behandlingslängd",
  plan: "Behandlingsplan",
} as const;

export const plannedMedicationVersionSchema = z.object({
  version: z.number().int().min(1),
  medication: z.string().nullable(),
  strength: z.string().nullable(),
  dose: z.string().nullable(),
  frequency: z.string().nullable(),
  duration: z.string().nullable(),
  plan: z.string().nullable(),
  source: z.enum(PLANNED_MEDICATION_SOURCES),
  // The doctor's medical reason, on a version that changed the plan.
  changeReason: z.string().nullable(),
  // TES-1292 — the catalogue version the parts were picked from (null: free
  // text), and the doctor's motivation when they deviate from it. Optional
  // so a reader of the older shape keeps parsing.
  medicationVersionId: z.string().nullable().optional(),
  deviationReason: z.string().nullable().optional(),
  recordedAt: z.string(),
  recordedByEmployeeId: z.string(),
});
export type PlannedMedicationVersion = z.infer<
  typeof plannedMedicationVersionSchema
>;

// GET employee-api/v1/patients/:id/planned-medication. `current` is the
// newest version; for a patient documented before TES-1185 it is read from
// her newest documented meeting (version 0, nothing stored) so the decision
// can still be prefilled. `versions` newest first.
export const plannedMedicationViewSchema = z.object({
  current: plannedMedicationVersionSchema
    .extend({ version: z.number().int().min(0) })
    .nullable(),
  versions: z.array(plannedMedicationVersionSchema),
});
export type PlannedMedicationView = z.infer<typeof plannedMedicationViewSchema>;

/**
 * TES-1185 — the order's four fields against the current plan. TES-1221 —
 * shared, so the admin asks for a change's reason exactly when the API would
 * refuse the order without one.
 */
export function orderMatchesPlan(
  order: {
    medication: string;
    dose: string;
    frequency: string;
    duration?: string | null;
  },
  plan: {
    medication: string | null;
    dose: string | null;
    frequency: string | null;
    duration: string | null;
  },
): boolean {
  const norm = (v: string | null | undefined) => (v ?? "").trim();
  return (
    norm(order.medication) === norm(plan.medication) &&
    norm(order.dose) === norm(plan.dose) &&
    norm(order.frequency) === norm(plan.frequency) &&
    norm(order.duration) === norm(plan.duration)
  );
}
